// The scene's pharma run as Drugs' panel and traces read it: its PK course and its targets' binding, at
// the Dose slider's dose where the course follows the model that rescales it (data/sims/dose.ts), and
// each target's central occupancy as a column of its own.

import { useMemo } from 'react';
import { useAtlas } from '../app/store.ts';
import { doseFactor } from '../app/scene.ts';
import { openedSlot } from '../app/sims.ts';
import { pkCourse } from '../data/sims/pharma.ts';
import { scaledCourse } from '../data/sims/dose.ts';
import { frameTimes } from '../data/sims/time.ts';
import { useLoaded } from '../ui/hooks.ts';

export function usePharmaCourse() {
  const main = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.slots.find((x) => x.slot === 'main' && x.format === 'pharma') : undefined));
  const o = main ? openedSlot('main') : undefined;
  const sim = o?.format === 'pharma' ? o.sim : null;
  const pk = useLoaded(() => (sim && !sim.snapshot && sim.header.files?.series?.length ? sim.series() : null), [sim]);
  const curves = useLoaded(() => (sim ? sim.bindingCurves() : null), [sim]);
  // The factor applies to the run the store has read (DrugState), not one still loading behind it.
  const factor = useAtlas((s) => (sim && s.drug?.run === sim.header.id ? doseFactor(s) : 1));
  const own = useMemo(() => (pk.value ? pkCourse(pk.value) : null), [pk.value]);
  const course = useMemo(() => (own && curves.value && factor !== 1 ? scaledCourse(own, curves.value, factor) : own), [own, curves.value, factor]);
  const times = useMemo(() => (sim ? frameTimes(sim.axis) : null), [sim]);
  const occupancy = useMemo(() => {
    const occ = course?.occupancy;
    if (!sim || !occ?.central) return null;
    const T = occ.targets.length;
    return new Map(occ.targets.map((t, k) => [t, Float32Array.from({ length: sim.axis.frames }, (_, f) => occ.central![f * T + k]!)]));
  }, [sim, course]);
  return { sim, pk, curves, course, factor, times, occupancy };
}
